import * as React from "react";
import Button from "@mui/joy/Button";
import Card from "@mui/joy/Card";
import CardContent from "@mui/joy/CardContent";
import Typography from "@mui/joy/Typography";
import CardCover from "@mui/joy/CardCover";
import Box from "@mui/joy/Box";
import { ModalDialog, ModalClose, Stack } from "@mui/joy";
import Modal from "@mui/joy/Modal";
import Divider from "@mui/joy/Divider";
import { UserAuth } from "../../context/AuthContext";
import { db } from "../../FirebaseConfig";
import { v4 as uuidv4 } from "uuid";
import LocalActivityIcon from "@mui/icons-material/LocalActivity";
import HowToRegIcon from "@mui/icons-material/HowToReg";
import firebase from "firebase/compat/app";
import { toast } from "react-toastify";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import CountdownTimer from "../Utils/CountDown";
import FormControl from "@mui/joy/FormControl";
import FormLabel from "@mui/joy/FormLabel";
import Input from "@mui/joy/Input";
import CardActions from "@mui/joy/CardActions";
import CardOverflow from "@mui/joy/CardOverflow";
import Checkbox from "@mui/joy/Checkbox";
import FormHelperText from "@mui/joy/FormHelperText";
import Link from "@mui/joy/Link";
import Autocomplete from "@mui/joy/Autocomplete";
import AutocompleteOption from "@mui/joy/AutocompleteOption";
import ListItemDecorator from "@mui/joy/ListItemDecorator";
import ListItemContent from "@mui/joy/ListItemContent";
import LinearProgress from "@mui/joy/LinearProgress";
import { GetCountries, GetState, GetCity } from "react-country-state-city";

interface Country {
  id: number;
  name: string;
  iso2: string;
  phone_code: number | string;
}

interface State {
  id: number;
  name: string;
}

interface City {
  id: number;
  name: string;
}

interface CustomEventCardProps {
  title: string;
  category: string;
  banner: string;
  data: {
    eventname: string;
    startdate: string;
    enddate: string;
    eventdescription: string;
    prize: string;
    organizer: string;
    requiresAdditionalDetails: boolean;
  };
}

const CustomEventCard = (props: CustomEventCardProps) => {
  const [open, setOpen] = React.useState(false);
  const [selectedData, setSelectedData] = React.useState<any>({});
  const { userData } = UserAuth();
  const [isRegistered, setIsRegistered] = React.useState(false);
  const uid = uuidv4();
  const [registerModalOpen, setRegisterModalOpen] = React.useState(false);
  const [phoneNumber, setPhoneNumber] = React.useState("");
  const [organization, setOrganization] = React.useState("");
  const [agreeToTerms, setAgreeToTerms] = React.useState(false);
  const [name, setName] = React.useState("");
  const [loadingCountries, setLoadingCountries] = React.useState<boolean>(true);
  const [loadingStates, setLoadingStates] = React.useState<boolean>(false);
  const [loadingCities, setLoadingCities] = React.useState<boolean>(false);
  const [selectedCountry, setSelectedCountry] = React.useState<Country | null>(
    null
  );
  const [selectedState, setSelectedState] = React.useState<State | null>(null);
  const [selectedCity, setSelectedCity] = React.useState<City | null>(null);
  const [countries, setCountries] = React.useState<Country[]>([]);
  const [states, setStates] = React.useState<State[]>([]);
  const [cities, setCities] = React.useState<City[]>([]);
  const [isFormValid, setIsFormValid] = React.useState(false);

  const [errors, setErrors] = React.useState({
    name: false,
    organization: false,
    phoneNumber: false,
    country: false,
    state: false,
    city: false,
  });

  React.useEffect(() => {
    const checkRegistration = () => {
      db.collection("Events")
        .doc(selectedData.eventname.toLowerCase())
        .collection("Registrations")
        .doc(userData?.id)
        .get()
        .then((doc) => {
          if (doc.exists) {
            setIsRegistered(true);
          } else {
            setIsRegistered(false);
          }
        })
        .catch((error) => {
          setIsRegistered(false);
          toast.error("Failed to fetch registration details");
        });
    };

    if (open) {
      checkRegistration();
    }
  }, [selectedData, userData, open]);

  const formatDateTime = (dateTimeString: any) => {
    const date = new Date(dateTimeString);
    const day = date.getDate().toString().padStart(2, "0");
    const month = (date.getMonth() + 1).toString().padStart(2, "0");
    const year = date.getFullYear().toString();
    return `${day}/${month}/${year}`;
  };

  const fetchCountries = async () => {
    try {
      const result = await GetCountries();
      setCountries(result);
    } catch (error) {
      toast.error("Error fetching countries");
    } finally {
      setLoadingCountries(false);
    }
  };

  React.useEffect(() => {
    validateForm();
  }, [
    name,
    organization,
    phoneNumber,
    selectedCountry,
    selectedState,
    selectedCity,
    agreeToTerms,
  ]);

  const handleInputChange =
    (setter: React.Dispatch<React.SetStateAction<any>>) =>
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setter(event.target.value);
      validateForm();
    };

  const handleCountryChange = async (
    event: React.SyntheticEvent,
    value: Country | null
  ) => {
    setSelectedCountry(value);
    setSelectedState(null);
    setSelectedCity(null);
    setStates([]);
    setCities([]);
    validateForm();

    if (value) {
      setLoadingStates(true);
      try {
        const result = await GetState(value.id);
        setStates(result);
      } catch (error) {
        toast.error("Error fetching states");
      } finally {
        setLoadingStates(false);
      }
    }
  };

  const handleStateChange = async (
    event: React.SyntheticEvent,
    value: State | null
  ) => {
    setSelectedState(value);
    setSelectedCity(null);
    setCities([]);
    validateForm();

    if (value && selectedCountry) {
      setLoadingCities(true);
      try {
        const result = await GetCity(selectedCountry.id, value.id);
        setCities(result);
      } catch (error) {
        toast.error("Error fetching cities");
      } finally {
        setLoadingCities(false);
      }
    }
  };

  const handleCityChange = (
    event: React.SyntheticEvent,
    value: City | null
  ) => {
    setSelectedCity(value);
    validateForm();
  };

  const handleRegister = () => {
    if (isRegistered) {
      toast.info("You are already registered.");
      return;
    }

    if (props.data.requiresAdditionalDetails) {
      if (countries.length === 0) {
        fetchCountries();
      }
      setRegisterModalOpen(true);
    } else {
      handleSubmit();
    }
  };

  const validateForm = () => {
    const isFormComplete =
      name.trim() !== "" &&
      organization.trim() !== "" &&
      /^\d{10}$/.test(phoneNumber) &&
      selectedCountry !== null &&
      agreeToTerms &&
      (!states.length || selectedState !== null) &&
      (!cities.length || selectedCity !== null);

    setIsFormValid(isFormComplete);
  };

  const handleSubmit = () => {
    if (props.data.requiresAdditionalDetails) {
      const newErrors = {
        name: name.trim() === "",
        organization: organization.trim() === "",
        phoneNumber: !/^\d{10}$/.test(phoneNumber),
        country: !selectedCountry,
        state: states.length > 0 && !selectedState,
        city: cities.length > 0 && !selectedCity,
      };
      setErrors(newErrors);

      if (Object.values(newErrors).some((error) => error)) {
        toast.error("Please fill out all required fields.");
        return;
      }
    }

    const registrationData = {
      event: props.title,
      username: userData?.name,
      userid: userData?.id,
      useremail: userData?.email,
      uid: uid,
      registeredAt: firebase.firestore.FieldValue.serverTimestamp(),
      points: props.category === "CTF" ? 0 : null,
      ...(props.data.requiresAdditionalDetails && {
        name,
        phoneNumber,
        country: selectedCountry,
        state: selectedState,
        city: selectedCity,
        agreeToTerms,
        organization,
      }),
    };

    db.collection("Events")
      .doc(props.title.toLowerCase())
      .collection("Registrations")
      .doc(userData?.id)
      .set(registrationData)
      .then(() => {
        toast.success("Registered Successfully!");
        setIsRegistered(true);
        setRegisterModalOpen(false);
      })
      .catch((error) => {
        toast.error("Error Registering!");
        setIsRegistered(false);
      });
  };

  return (
    <>
      <Card variant="solid" invertedColors>
        <CardCover>
          <img
            src={
              props.banner ||
              "https://images.unsplash.com/photo-1502657877623-f66bf489d236?auto=format&fit=crop&w=800"
            }
            loading="lazy"
            alt="event banner"
          />
          <div
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              backgroundColor: "rgba(0, 0, 0, 0.5)", // Dark overlay with adjustable opacity
            }}
          ></div>
        </CardCover>
        <CardContent
          orientation="horizontal"
          style={{ display: "flex", justifyContent: "space-between" }}
        >
          <Typography level="title-lg">
            {props.title} <br />
          </Typography>
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
            }}
          >
            <Typography level="title-md">
              Ends in: <br />
            </Typography>
            <span>
              <CountdownTimer
                startTime={props.data.startdate}
                endTime={props.data.enddate}
              />{" "}
              <br />
            </span>
          </div>
        </CardContent>
        <Typography level="h2">{props.title}</Typography>
        <CardContent orientation="horizontal">
          <div
            style={{
              textAlign: "center",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
            }}
          >
            <Typography level="title-md">Category</Typography>
            <Typography level="title-sm">
              {props.category} <br />
            </Typography>
          </div>
          <Button
            variant="plain"
            size="md"
            aria-label="Explore"
            sx={{ ml: "auto", alignSelf: "center", fontWeight: 600 }}
            onClick={() => {
              setOpen(true);
              setSelectedData(props.data);
            }}
          >
            Explore
          </Button>
        </CardContent>
      </Card>
      <div>
        <Modal
          aria-labelledby="Event Modal"
          aria-describedby="This modal shows Event data."
          open={open}
          onClose={() => {
            setOpen(false);
          }}
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            transform: "translateX(120px)",
          }}
        >
          <ModalDialog
            layout={"center"}
            sx={{
              backgroundImage: `url('${
                props.banner
                  ? props.banner
                  : "https://images.unsplash.com/photo-1502657877623-f66bf489d236?auto=format&fit=crop&w=800"
              }')`,
              backgroundSize: "cover",
              backgroundPosition: "center",
              backgroundRepeat: "no-repeat",
              borderRadius: "md",
              p: 2,
              overflow: "auto",
              "&::before": {
                content: '""',
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: "rgba(0, 0, 0, 0.8)",
                borderRadius: "inherit",
              },
              "& > *": {
                position: "relative",
              },
            }}
          >
            <div>
              <Typography
                textColor={"#fff"}
                startDecorator={<LocalActivityIcon style={{ color: "#fff" }} />}
                level="h2"
              >
                Event Details
              </Typography>
              <ModalClose />
            </div>
            <Box
              sx={{
                width: "700px",
                display: "flex",
              }}
            >
              <Stack spacing={1} width={"100%"} divider={<Divider />}>
                <Stack
                  spacing={1}
                  direction="row"
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    padding: "20px",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                    }}
                  >
                    <Typography level="title-lg" textColor={"#fff"}>
                      Category <br />
                    </Typography>
                    <Typography
                      level="body-lg"
                      textColor="rgba(255,255,255,0.8)"
                    >
                      {selectedData.category}
                    </Typography>
                  </div>
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                    }}
                  >
                    <Typography level="title-lg" textColor={"#fff"}>
                      Starts on <br />
                    </Typography>
                    <Typography
                      level="body-lg"
                      textColor="rgba(255,255,255,0.8)"
                    >
                      {formatDateTime(selectedData.startdate)}
                    </Typography>
                  </div>
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                    }}
                  >
                    <Typography level="title-lg" textColor={"#fff"}>
                      Ends on <br />
                    </Typography>
                    <Typography
                      level="body-lg"
                      textColor="rgba(255,255,255,0.8)"
                    >
                      {formatDateTime(selectedData.enddate)}
                    </Typography>
                  </div>
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                    }}
                  >
                    <Typography level="title-lg" textColor={"#fff"}>
                      Duration <br />
                    </Typography>
                    <Typography
                      level="body-lg"
                      textColor="rgba(255,255,255,0.8)"
                    >
                      <CountdownTimer
                        startTime={selectedData.startdate}
                        endTime={selectedData.enddate}
                      />
                    </Typography>
                  </div>
                </Stack>
                <Stack
                  spacing={1}
                  direction="row"
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    padding: "20px",
                    paddingTop: "0px",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                    }}
                  >
                    <Typography
                      level="h1"
                      sx={{ mb: 2 }}
                      textColor="rgba(255,255,255)"
                    >
                      {selectedData.eventname}
                    </Typography>
                    <Typography
                      level="title-lg"
                      sx={{ mb: 0.5 }}
                      textColor={"#fff"}
                    >
                      Description
                    </Typography>
                    <Typography
                      textColor="rgba(255,255,255,0.8)"
                      level="body-md"
                      style={{
                        height: "150px",
                        overflowY: "auto",
                        paddingRight: "10px",
                      }}
                    >
                      {selectedData.eventdescription}
                    </Typography>
                  </div>
                </Stack>
                <Stack
                  spacing={1}
                  direction="column"
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    padding: "20px",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      alignContent: "center",
                    }}
                  >
                    <Typography
                      level="title-lg"
                      startDecorator={
                        <EmojiEventsIcon sx={{ color: "#fff" }} />
                      }
                      textColor={"#fff"}
                    >
                      Prize <br />
                    </Typography>
                    <Typography
                      textColor="rgba(255,255,255,0.8)"
                      level="body-md"
                    >
                      {selectedData.prize}
                      <br />
                    </Typography>
                  </div>
                </Stack>
                <Stack
                  spacing={1}
                  direction="row"
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    padding: "20px",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                    }}
                  >
                    <Typography level="title-lg" textColor={"#fff"}>
                      Organized by: <br />
                    </Typography>
                    <Typography
                      level="body-lg"
                      textColor="rgba(255,255,255,0.8)"
                    >
                      {selectedData.organizer}
                    </Typography>
                  </div>
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                    }}
                  >
                    <Button
                      onClick={() => {
                        handleRegister();
                      }}
                      variant="plain"
                      aria-label="Register Now!"
                      disabled={isRegistered}
                      sx={{
                        ml: "auto",
                        alignSelf: "center",
                        height: "100%",
                        fontWeight: 600,
                      }}
                      size="md"
                      startDecorator={<HowToRegIcon />}
                    >
                      {isRegistered ? "Already Registered!" : "Register Now!"}
                    </Button>
                  </div>
                </Stack>
              </Stack>
            </Box>
          </ModalDialog>
        </Modal>
        <Modal
          open={registerModalOpen}
          onClose={() => setRegisterModalOpen(false)}
          aria-labelledby="register-modal"
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            transform: "translateX(120px)",
          }}
        >
          <ModalDialog
            layout={"center"}
            sx={{
              backgroundSize: "cover",
              backgroundPosition: "center",
              backgroundRepeat: "no-repeat",
              borderRadius: "md",
              p: 2,
              overflow: "auto",
            }}
          >
            <Stack
              spacing={4}
              sx={{
                display: "flex",
                maxWidth: "800px",
                mx: "auto",
              }}
            >
              <Card>
                <Box sx={{ mb: 1 }}>
                  <Typography level="title-md">
                    Register for {props.title}
                  </Typography>
                  <Typography level="body-sm">
                    Please fill out the form below to register for the event.
                  </Typography>
                </Box>
                <Divider />
                <Stack spacing={2}>
                  <FormControl>
                    <FormLabel>
                      Name<span style={{ color: "red" }}> *</span>
                    </FormLabel>
                    <Input
                      placeholder="Name"
                      value={name}
                      onChange={handleInputChange(setName)}
                    />
                  </FormControl>

                  <FormControl>
                    <FormLabel>
                      Organization Name<span style={{ color: "red" }}> *</span>
                    </FormLabel>
                    <Input
                      placeholder="Organization"
                      value={organization}
                      onChange={handleInputChange(setOrganization)}
                    />
                  </FormControl>
                  <FormControl>
                    <FormLabel>
                      Phone Number<span style={{ color: "red" }}> *</span>
                    </FormLabel>
                    <Input
                      placeholder="Phone Number"
                      value={phoneNumber}
                      onChange={handleInputChange(setPhoneNumber)}
                    />
                  </FormControl>

                  <FormControl>
                    <FormLabel>
                      Country<span style={{ color: "red" }}> *</span>
                    </FormLabel>
                    <Autocomplete
                      placeholder="Choose a country"
                      options={countries}
                      autoHighlight
                      value={selectedCountry}
                      onChange={handleCountryChange}
                      getOptionLabel={(option) => option.name}
                      renderOption={(props, option) => (
                        <AutocompleteOption {...props}>
                          <ListItemDecorator>
                            <img
                              loading="lazy"
                              width="20"
                              srcSet={`https://flagcdn.com/w40/${option.iso2.toLowerCase()}.png 2x`}
                              src={`https://flagcdn.com/w20/${option.iso2.toLowerCase()}.png`}
                              alt=""
                            />
                          </ListItemDecorator>
                          <ListItemContent sx={{ fontSize: "sm" }}>
                            {option.name}
                            <Typography level="body-xs">
                              ({option.iso2}) +{option.phone_code}
                            </Typography>
                          </ListItemContent>
                        </AutocompleteOption>
                      )}
                    />
                  </FormControl>
                  <FormControl>
                    <FormLabel>
                      State<span style={{ color: "red" }}> *</span>
                    </FormLabel>
                    {loadingStates ? (
                      <LinearProgress />
                    ) : (
                      <Autocomplete
                        placeholder="Choose a state"
                        options={states}
                        getOptionLabel={(option) => option.name}
                        value={selectedState}
                        onChange={handleStateChange}
                        disabled={!selectedCountry}
                      />
                    )}
                  </FormControl>
                  <FormControl>
                    <FormLabel>
                      City<span style={{ color: "red" }}> *</span>
                    </FormLabel>
                    {loadingCities ? (
                      <LinearProgress />
                    ) : (
                      <Autocomplete
                        placeholder="Choose a city"
                        options={cities}
                        getOptionLabel={(option) => option.name}
                        value={selectedCity}
                        onChange={handleCityChange}
                        disabled={!selectedState}
                      />
                    )}
                  </FormControl>
                </Stack>
                <FormControl
                  size="sm"
                  sx={{
                    width: 400,
                    alignContent: "center",
                    alignItems: "center",
                    py: 1,
                  }}
                >
                  <Checkbox
                    checked={agreeToTerms}
                    onChange={(e) => setAgreeToTerms(e.target.checked)}
                    label={
                      <React.Fragment>
                        I have read and agree to the{" "}
                        <Typography sx={{ fontWeight: "md" }}>
                          terms and conditions
                        </Typography>
                        .<span style={{ color: "red" }}> *</span>
                      </React.Fragment>
                    }
                  />
                  <FormHelperText>
                    <Typography level="body-sm">
                      Read our<Link href="/termsofservice" underline="hover" target="_blank" rel="noopener noreferrer">terms and conditions</Link>.
                    </Typography>
                  </FormHelperText>
                </FormControl>
                <CardOverflow
                  sx={{ borderTop: "1px solid", borderColor: "divider" }}
                >
                  <CardActions sx={{ alignSelf: "flex-end", pt: 2 }}>
                    <Button
                      size="sm"
                      variant="outlined"
                      color="neutral"
                      onClick={() => setRegisterModalOpen(false)}
                    >
                      Cancel
                    </Button>
                    <Button
                      size="sm"
                      variant="solid"
                      onClick={handleSubmit}
                      disabled={!isFormValid}
                    >
                      Register
                    </Button>
                  </CardActions>
                </CardOverflow>
              </Card>
            </Stack>
          </ModalDialog>
        </Modal>
      </div>
    </>
  );
};

export default CustomEventCard;